import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {getVenueSession} from "../../requests/AppSession";
import dateFormat from "dateformat";
import {NumericFormat} from "react-number-format";
import {Combobox} from '@headlessui/react'
import {CheckIcon, ChevronUpDownIcon} from "@heroicons/react/20/solid";
import {findVenueDrinkHistory, getVenueMonths} from "../requests/AppRequestVenueDashboard";
import {HistorySkeleton} from "./VTSkeletons";
import {useAuth0} from "@auth0/auth0-react";


function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function VTVenueDrinkHistory() {
    const {isLoading, getAccessTokenSilently} = useAuth0();

    let navigate = useNavigate();

    const [venueId, setVenueId] = React.useState(null);
    const [venueName, setVenueName] = React.useState(null);
    const [buyHistory, setBuyHistory] = useState(null);
    const [dates, setDates] = useState([]);
    const [datesLoaded, setDatesLoaded] = useState(false);
    const [monthNumber, setMonthNumber] = useState(null);
    const [yearNumber, setYearNumber] = useState(null);

    const [errorMessage, setErrorMessage] = React.useState(null);

    const getDatesSuccess = async responseJson => {
        if (
            responseJson
        ) {
            setDates(responseJson);
            setDatesLoaded(true);
            if (responseJson.length > 0) {
                setSelectedDate(responseJson[0]);
                setMonthNumber(responseJson[0].monthNumber);
                setYearNumber(responseJson[0].yearNumber)
            }
        } else {
            setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
        }
    };

    const getDatesError = forceLogout => {
        setErrorMessage("Error accessing venue dates, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.id !== "null") {
                setVenueId(venueSession.venueId);
                setVenueName(venueSession.venueName);
            } else {
                navigate('/login/mobile', {replace: true});
            }

            if (!venueSession.drinkPayFeatureFlag) {
                navigate('/login/mobile', {replace: true});
            }

        })

        getVenueMonths(getDatesSuccess, getDatesError, getAccessTokenSilently);

        return function cleanup() {

        };
    }, []);

    const getSuccess = async responseJson => {
        if (
            responseJson
        ) {
            setBuyHistory(responseJson);
        } else {
            setErrorMessage("Error accessing venue sell activity, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setErrorMessage("Error accessing venue sell activity, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (venueId !== null && monthNumber !== null && yearNumber !== null) {
            // Reset so the spinner shows and the previous month's data doesn't linger while loading.
            setBuyHistory(null);
            findVenueDrinkHistory(venueId, monthNumber, yearNumber, getSuccess, getError, getAccessTokenSilently);
            //findVenueSellPurchases(venueId, monthNumber, yearNumber, getSuccess, getError);
            //findVenueSellPurchaseHistory(venueId, monthNumber, yearNumber, getSuccess, getError);
        }
        return function cleanup() {

        };
    }, [venueId, monthNumber, yearNumber]);

    const [query, setQuery] = useState('')
    const [selectedDate, setSelectedDate] = useState(null)

    const filteredDates =
        query === ''
            ? dates
            : dates.filter((date) => {
                return date.name.toLowerCase().includes(query.toLowerCase())
            })

    const hasRows = buyHistory && buyHistory.length > 0;
    // Only treat an empty month list as "no activity" once the dates fetch has actually
    // completed — otherwise the empty state flashes before data loads.
    const hasNoMonths = datesLoaded && dates.length === 0;
    const totalVt = hasRows ? buyHistory.reduce((a, c) => a + c.vtAmount, 0) : 0;
    const totalVenue = hasRows ? buyHistory.reduce((a, c) => a + c.amountToVenue, 0) : 0;

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-3xl">Drink Activity</h1>
                    {/*<h1 className="text-base font-semibold leading-6 text-gray-900">Invoice</h1>*/}
                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">

                        <Combobox as="div" value={selectedDate} onChange={(value) => {
                            setSelectedDate(value);
                            if (value) {
                                setMonthNumber(value.monthNumber);
                                setYearNumber(value.yearNumber);
                            }
                        }}>
                            <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                Month</Combobox.Label>
                            <div className="relative mt-2">
                                <Combobox.Input
                                    className="w-full rounded-md border-0 bg-white dark:bg-gray-800 py-1.5 pl-3 pr-12 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-pink-500 sm:text-sm sm:leading-6"
                                    onChange={(event) => {
                                        setQuery(event.target.value);
                                    }}
                                    displayValue={(person) => person?.name}
                                />
                                <Combobox.Button
                                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                </Combobox.Button>

                                {filteredDates && filteredDates.length > 0 && (
                                    <Combobox.Options
                                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {filteredDates && filteredDates.map((person) => (
                                            <Combobox.Option
                                                key={person.name}
                                                value={person}
                                                className={({active}) =>
                                                    classNames(
                                                        'relative cursor-default select-none py-2 pl-3 pr-9',
                                                        active ? 'bg-pink-700 text-white' : 'text-gray-900 dark:text-gray-100'
                                                    )
                                                }
                                            >
                                                {({active, selected}) => (
                                                    <>
                                                        <div className="flex">
                                                            <span
                                                                className={classNames('truncate', selected && 'font-semibold')}>{person.name}</span>
                                                            <span
                                                                className={classNames(
                                                                    'ml-2 truncate text-gray-500 dark:text-gray-400',
                                                                    active ? 'text-pink-200' : 'text-gray-500 dark:text-gray-400'
                                                                )}
                                                            >
                        {person.username}
                      </span>
                                                        </div>

                                                        {selected && (
                                                            <span
                                                                className={classNames(
                                                                    'absolute inset-y-0 right-0 flex items-center pr-4',
                                                                    active ? 'text-white' : 'text-pink-700'
                                                                )}
                                                            >
                        <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                      </span>
                                                        )}
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))}
                                    </Combobox.Options>
                                )}
                            </div>
                        </Combobox>

                        {!buyHistory && !hasNoMonths && <div className="mt-4"><HistorySkeleton/></div>}

                        {hasNoMonths && <h3 className="mt-2 text-gray-500 dark:text-gray-400">No drink activity yet.</h3>}
                        {buyHistory && buyHistory.length === 0 && !hasNoMonths &&
                            <h3 className="mt-2 text-gray-500 dark:text-gray-400">No drink activity for this month.</h3>}

                        {hasRows &&
                            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="rounded-xl bg-pink-600 px-5 py-4 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-pink-100">Venue Profit</p>
                                    <p className="mt-1 text-2xl font-bold text-white">
                                        <NumericFormat value={totalVenue} displayType={'text'} decimalScale={2}
                                                       fixedDecimalScale={true} thousandSeparator={true} prefix={'$'}/>
                                    </p>
                                </div>
                                <div className="rounded-xl bg-white dark:bg-gray-800 px-5 py-4 ring-1 ring-gray-200 dark:ring-gray-700">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Total VT</p>
                                    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                                        <NumericFormat value={totalVt} displayType={'text'} decimalScale={0}
                                                       fixedDecimalScale={true} thousandSeparator={true} suffix={' VT'}/>
                                    </p>
                                </div>
                            </div>}
                    </div>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    {/*<button*/}
                    {/*    type="button"*/}
                    {/*    className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"*/}
                    {/*>*/}
                    {/*    Print*/}
                    {/*</button>*/}
                </div>
            </div>
            {buyHistory && buyHistory.length > 0 && <div className="mt-8 overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700">
                <table className="min-w-full">
                    <colgroup>
                        <col className="w-full sm:w-1/2"/>
                        <col className="sm:w-1/6"/>
                        <col className="sm:w-1/6"/>
                        <col className="sm:w-1/6"/>
                    </colgroup>
                    <thead className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                    <tr>
                        <th scope="col"
                            className="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Drink Name
                        </th>
                        <th
                            scope="col"
                            className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 sm:table-cell"
                        >

                        </th>
                        <th
                            scope="col"
                            className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 sm:table-cell"
                        >
                            VT
                        </th>
                        <th scope="col"
                            className="py-3.5 pl-3 pr-6 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Profit
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {buyHistory && buyHistory.map((history) => (
                        <tr key={history.id} className="border-b border-gray-200 dark:border-gray-700">
                            <td className="max-w-0 py-5 pl-6 pr-3 text-sm">
                                <div className="font-medium text-gray-900 dark:text-gray-100">{history.drinkName}</div>
                                <div
                                    className="mt-1 truncate text-gray-500 dark:text-gray-400">{dateFormat(history.purchaseDate, "mmmm dS, yyyy")}</div>
                            </td>
                            <td className="px-3 py-5 text-right text-sm text-gray-500 dark:text-gray-400 sm:table-cell">
                            </td>
                            <td className="px-3 py-5 text-right text-sm text-gray-500 dark:text-gray-400 sm:table-cell">
                                {history.vtAmount}
                            </td>
                            <td className="py-5 pl-3 pr-6 text-right text-sm text-gray-500 dark:text-gray-400">
                                <NumericFormat value={history.amountToVenue} displayType={'text'}
                                               decimalScale={2} fixedDecimalScale={true}
                                               thousandSeparator={true} prefix={'$'}/>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>}
        </div>
    )
}
